<template>
  <div :class="$style.checkoutPage">
    <v-layout row>
      <v-flex :class="$style.appGrid">
        <form id="payment-form"
              :class="$style.paymentForm"
              @submit="confirmOrder">
          <h3>Payment Information</h3>
          <div :class="$style.paymentInfo">
            <div :class="$style.paymentInfoField">
              <div :class="$style.longField">
                <InputField :class="getInputFieldClasses($v.name.$error)"
                            v-model="$v.name.$model"
                            label="Name on Card*"/>
                <p v-if="$v.name.$error"
                   :class="$style.errorMessage">
                  Name is required
                </p>
              </div>
            </div>

            <div :class="$style.paymentInfoField">
              <div id="card-element">
                <!-- A Stripe Element will be inserted here. -->
                <div :class="$style.longField"
                     class="formField">
                  <div id="card-number"
                       class="input"/>
                  <label for="card-number">Credit Card Number*</label>
                  <div class="baseline"/>
                  <div id="card-number-errors"
                       class="card-error-message"/>

                  <div id="card-common-errors"
                       class="card-error-message common-messages" />
                </div>

                <div :class="$style.shortField">
                  <div class="formField">
                    <div id="card-exp"
                         class="input"/>
                    <label for="card-exp">Exp Date*</label>
                    <div class="baseline"/>
                    <div id="card-exp-errors"
                         class="card-error-message"/>
                  </div>

                  <div class="formField">
                    <div id="card-cvc"
                         class="input"/>
                    <label for="card-cvc">CVV*</label>
                    <div class="baseline"/>
                    <div id="card-cvc-errors"
                         class="card-error-message"/>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <h3>Confirm Order Info</h3>
          <div :class="$style.paymentInfoField">
            <div :class="$style.longField">
              <SelectField :class="getSelectStyle($v.time.$error)"
                           v-model="$v.time.$model"
                           :items="orderPickupTimes"
                           label="Pickup Time*"
                           @dataChanged="updateOrder('pickup_time', $event)"/>
              <p v-if="$v.time.$error"
                 :class="$style.errorMessage">
                Time is required
              </p>
            </div>
            <div v-if="!noPossibleOptions"
                 :class="$style.shortField">
              <div :class="$style.formRadio">
                <h6>Select an Option</h6>
                <v-radio-group :class="$style.formSelectGroup"
                               v-model="orderType"
                               row
                               @change="updateOrder('pickup_type', $event)">
                  <v-radio v-if="restaurantOptions.pickup"
                           :disabled="restaurantOptions.pickup
                           && !restaurantOptions.curbside"
                           label="In-store Pick-Up"
                           value="in_store"
                           color="#000000"/>
                  <v-radio v-if="restaurantOptions.curbside"
                           :disabled="restaurantOptions.curbside
                           && !restaurantOptions.pickup"
                           label="Curbside Pick-up"
                           value="curbside"
                           color="#000000"/>
                </v-radio-group>
              </div>
            </div>
          </div>

          <div v-show="supportGooglePay"
               ref="google-pay-btn-container"
               :class="[$style.googlePayBtn, $style.mobilePayBtn]"/>

          <div v-show="supportApplePay"
               :class="[$style.applePayBtn, $style.mobilePayBtn]"
               @click="handleApplePayPayment"/>

          <CartTotal v-if="!isKioskPlatform && isMediumResolution"
                     :class="$style.mobileTotal"
                     :current-page="$route.name"
                     total-info />

          <MobileButton v-if="isMobilePlatform"
                        :class="$style.mobileBtn"
                        :disabled="isLoading"
                        absolute-position="bottom"
                        type="submit"
                        square>
            Confirm Payment
          </MobileButton>
          <div v-if="!isMobilePlatform"
               :class="$style.checkoutButtons">
            <BaseButton :class="$style.cancelBtn"
                        :disabled="isLoading"
                        button-style="Back"
                        @click="handleCancel">
              Cancel
            </BaseButton>
            <BaseButton :class="$style.submitBtn"
                        :disabled="isLoading"
                        button-style="Main"
                        type="submit">
              <span>Submit</span>
              <svg v-svg
                   :class="$style.nextIcon"
                   symbol="straight-arrow"/>
            </BaseButton>
          </div>
        </form>
      </v-flex>
    </v-layout>

    <LoadingSpinner v-if="isMobilePlatform && isLoading"/>

    <CommonModal v-if="!isMobilePlatform && !isKioskPlatform"
                 :display="showPickupTimeAlert"
                 :always-modal="true"
                 :title="pickupTimeAlertContent.title"
                 width="480"
                 type="AppendModal"
                 name="linkSent">
      <ConfirmModalContent
        :message="pickupTimeAlertContent.msg"
        confirm-text="Yes"
        cancel-text="NO"
        @confirm="closePickupTimeAlert"
        @cancel="goBack"/>
    </CommonModal>

    <MobileModal v-if="isMobilePlatform"
                 v-model="showPickupTimeAlert"
                 dialog
                 center-content>
      <MobileConfirmDialog :title="pickupTimeAlertContent.title"
                           cancel-text="No"
                           confirm-text="Yes"
                           @confirm="closePickupTimeAlert"
                           @cancel="goBack">
        {{ pickupTimeAlertContent.msg }}
      </MobileConfirmDialog>
    </MobileModal>
  </div>
</template>

<script>
/* global Stripe */
/* global device */
/* global ApplePay */
import Component from 'vue-class-component';
import { Ref } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import to from 'await-to-js';
import Vue from 'vue';
import moment from 'moment';
import { required } from 'vuelidate/lib/validators';
import BaseButton from '@/components/BaseButton';
import InputField from '@/components/InputField';
import SelectField from '@/components/SelectField';
import MobileButton from '@/apps/mobile/components/MobileButton';
import authMixins from '@/apps/auth/mixins';
import {
  CART_DISPLAY,
  ORDER_PAY,
  ORDER_CREATE,
  ORDER_SET_TYPE,
  ORDER_PATCH,
  ORDER_UPDATE_ID,
  UPDATE_DISCOUNT,
  PAYMENT_INTENT,
} from '@/apps/cart/store/types';
import { API_ERROR_SET } from '@/apps/core/store/types';
import { AUTH_GUEST_TOKEN } from '@/apps/auth/store/types';
import config from '@/config';
import LoadingMixin from '@/lib/mixins/loading-mixin';
import CartTotal from '@/apps/cart/components/CartTotal';
import LoadingSpinner from '@/apps/configurator/components/LoadingSpinner';
import CommonModal from '@/apps/shared/components/CommonModal';
import ConfirmModalContent from '@/components/ConfirmModalContent';
import MobileModal from '@/apps/mobile/components/MobileModal';
import MobileConfirmDialog from '@/apps/mobile/components/MobileConfirmDialog';
import { Stripe as StripePlugin, ApplePayEventsEnum, GooglePayEventsEnum } from '@capacitor-community/stripe';

const MEDIUM_RESOLUTION = 1080;

Component.registerHooks([
  'beforeRouteLeave',
]);

/**
 * Page to display and edit users account data.
 */
@Component({
  components: {
    MobileConfirmDialog,
    MobileModal,
    ConfirmModalContent,
    CommonModal,
    LoadingSpinner,
    CartTotal,
    BaseButton,
    InputField,
    SelectField,
    MobileButton,
  },
  mixins: [
    authMixins.AuthorizationMixin,
    LoadingMixin,
  ],
  validations: {
    name: { required },
    time: { required },
  },
})
export default class CheckoutPayment extends Vue {
  @Action(CART_DISPLAY) cartDisplay;
  @Action(ORDER_PAY) orderPay;
  @Action(ORDER_CREATE) orderCreate;
  @Action(ORDER_SET_TYPE) setOrderType;
  @Action(ORDER_PATCH) patchOrder;
  @Action(AUTH_GUEST_TOKEN) getNewGuestToken;
  @Action(API_ERROR_SET) setApiError;
  @Action(ORDER_UPDATE_ID) updateOrderId;
  @Action(UPDATE_DISCOUNT) updateDiscount;
  @Action(PAYMENT_INTENT) paymentIntent;

  @Getter cart;
  @Getter doesDeviceSupportCurbsidePickup;
  @Getter restaurants;
  @Getter orderTotalPrice;
  @Getter orderItemPrice;
  @Getter restaurantId;
  @Getter isMobilePlatform;
  @Getter isKioskPlatform;
  @Getter isGuest;
  @Getter deviceInfo;
  @Getter('orderType') cartOrderType;
  @Getter orderTimes;
  @Getter order;

  @Ref('google-pay-btn-container') googlePayBtnContainer;

  name = '';
  time = '';
  nativePayment = false;
  stripe = null;
  showPickupTimeAlert = false;

  supportGooglePay = false;
  supportApplePay = false;

  isMediumResolution = false;
  restaurantOptions = null;

  get client() {
    if (this.isMobilePlatform && this.deviceInfo.platform === 'Android') {
      return window.cordova.plugins.payment.GooglePay;
    }

    if (this.isMobilePlatform && this.deviceInfo.platform === 'iOS') {
      return ApplePay.ApplePay;
    }

    return null;
  }

  get orderType() {
    return this.cartOrderType;
  }

  set orderType(value) {
    this.setOrderType(value);
  }

  get orderPickupTimes() {
    return Object.keys(this.orderTimes);
  }

  get pickupTimeAlertContent() {
    return {
      msg: `
        This location is no longer taking orders today.
        Place your order tomorrow?
      `,
      title: 'Pickup Time Confirmation',
    };
  }

  /**
   * Is available pickup today.
   *
   * @return {boolean}
   */
  get isAvailablePickupToday() {
    let available = false;

    Object.keys(this.orderTimes).every((key) => {
      const pickupDate = this.orderTimes[key];
      const currentRestaurantDate = moment().utcOffset(pickupDate.utcOffset());

      if (pickupDate.isSame(currentRestaurantDate, 'day')) {
        available = true;

        return false;
      }

      return true;
    });

    return available;
  }

  closePickupTimeAlert() {
    this.showPickupTimeAlert = false;
  }

  goBack() {
    this.$router.go(-1);
  }

  /**
   * get Classes for Input field, depended from error
   * @param {Boolean} isError
   */
  getInputFieldClasses(isError) {
    return [
      this.$style.formField,
      { [this.$style.errorField]: isError },
    ];
  }

  /**
   * get Classes for Select field, depended from error
   * @param {Boolean} isError
   */
  getSelectStyle(isError) {
    return [
      this.$style.formSelect,
      { [this.$style.errorSelect]: isError },
    ];
  }

  /**
   * Handler for "Cancel" button
   */
  async handleCancel() {
    this.$router.push({ name: 'menu' });
    this.clearFields();
    this.cartDisplay(true);
  }

  /**
   * Update oder data with time
   * @param data
   */
  async updateOrder(field, data) {
    let preparedData = data;

    if (field === 'pickup_time') {
      preparedData = this.orderTimes[data];
    }

    await this.patchOrder({
      [field]: preparedData,
    });
  }

  /**
   * Checks the current screen resolution
   */
  checkIfMediumResolution() {
    const documentWidth = document.documentElement.clientWidth;

    this.isMediumResolution = documentWidth <= MEDIUM_RESOLUTION;
  }

  /**
   * Gets possible restaurant options to choose
   */
  getRestaurantOptions() {
    const currentStore = this.restaurants
      .find(restaurant => restaurant.id === this.restaurantId);

    this.restaurantOptions = {
      curbside: currentStore.isCurbsideSupported && this.doesDeviceSupportCurbsidePickup,
      pickup:   currentStore.isPickupSupported,
    };
  }

  /**
   * Returns true if there are no possible options for the current restaurant
   */
  get noPossibleOptions() {
    if (this.restaurantOptions == null) {
      return true;
    }


    return !this.restaurantOptions.pickup || !this.restaurantOptions.curbside;
  }

  /**
   * Initialize Stripe plugin for the capacitor.
   * Native Stripe plugin doesn't allow apple pay payment.
   * Because Stripe expects the payment to go through https.
   * This is one of the requirements to provide support apple pay for the web.
   * Capacitor stripe plugin allows to call native iOS apple pay or android google pay APIs.
   */
  async initializeStripePlugin() {
    StripePlugin.initialize({
      publishableKey: config.stripePK,
    });

    this.supportApplePay = await StripePlugin.isApplePayAvailable()
      .then(() => true)
      .catch(() => false);

    this.supportGooglePay = await StripePlugin.isGooglePayAvailable()
      .then(() => true)
      .catch(() => false);

    if (this.supportGooglePay) {
      this.renderGooglePayBtn();
    }
  }

  /** Get payment intent for the order. */
  async getPaymentIntent() {
    const [ , res ] = await to(this.paymentIntent({}));

    if (res && res.error) {
      this.setApiError({ detail: res.error });
    }

    return res;
  }

  /** Handle apple pay payment. */
  async handleApplePayPayment() {
    this.loadingStart();

    const restaurant = this.restaurants
      .find(restaurant => restaurant.id === this.restaurantId);
    const paymentIntent = await this.getPaymentIntent();

    await StripePlugin.createApplePay({
      merchantIdentifier:        config.merchantIdentifier,
      paymentIntentClientSecret: paymentIntent.paymentIntentClientSecret,
      currency:                  'USD',
      countryCode:               'US',
      paymentSummaryItems:       [
        {
          label:  'Subtotal',
          amount: Number(this.order.subtotal),
        },
        {
          label:  'Tax',
          amount: Number(this.order.tax),
        },
        {
          label:  'Discount',
          amount: Number(this.order.discountPrice),
        },
        {
          label:  restaurant.name,
          amount: Number(this.order.totalPrice),
        },
      ],
    });

    const { paymentResult } = await StripePlugin.presentApplePay();

    switch (paymentResult) {
      case ApplePayEventsEnum.Completed:
        this.handlePaymentFinish();
        this.$router.push({
          name: 'order-confirmation',
        });
        break;

      case ApplePayEventsEnum.Canceled:
        this.setApiError({ detail: 'The payment has been cancelled.' });
        break;

      default:
        this.setApiError({ detail: 'There was an error trying to complete the payment, please try again later.' });
        break;
    }

    this.loadingStop();
  }


  /** Handle google pay payment. */
  async handleGooglePayPayment() {
    this.loadingStart();

    const paymentIntent = await this.getPaymentIntent();

    await StripePlugin.createGooglePay({
      paymentIntentClientSecret: paymentIntent.paymentIntentClientSecret,
    });

    const { paymentResult } = await StripePlugin.presentGooglePay();

    switch (paymentResult) {
      case GooglePayEventsEnum.Completed:
        this.handlePaymentFinish();
        this.$router.push({
          name: 'order-confirmation',
        });
        break;

      case GooglePayEventsEnum.Canceled:
        this.setApiError({ detail: 'The payment has been cancelled.' });
        break;

      default:
        this.setApiError({ detail: 'There was an error trying to complete the payment, please try again later.' });
        break;
    }

    this.loadingStop();
  }

  mounted() {
    this.getRestaurantOptions();
    this.checkIfMediumResolution();
    this.initializeStripePlugin();

    this.$nextTick(() => {
      window.addEventListener('resize', () => {
        this.checkIfMediumResolution();
      });
    });

    // Creating Stripe Payment component
    /* global Stripe */
    const stripe = Stripe(config.stripePK);

    this.stripe = stripe;

    if (!this.isAvailablePickupToday) {
      this.showPickupTimeAlert = true;
    }

    const elements = stripe.elements({
      // Stripe's examples are localized to specific languages
      locale: 'en',
      fonts:  [
        {
          family: 'Open Sans',
          cssSrc: 'https://fonts.googleapis.com/css' +
            '?family=Open+Sans:400,700&amp;subset=latin-ext',
          weight:       300,
          style:        'normal',
          unicodeRange: 'U+0000-00FF, U+0131, U+0152-0153, U+02C6, U+02DA, ' +
            'U+02DC, U+2000-206F, U+2074, U+20AC, U+2212, U+2215',
        },
      ],
    });

    const elementStyles = {
      base: {
        color:           'rgba(0,0,0,.87)',
        fontFamily:      'Open Sans',
        fontSize:        '13px',
        '::placeholder': {
          color: '#CFD7DF',
        },
      },
      invalid: {
        color: 'rgba(0,0,0,.87)',
      },
    };

    const elementClasses = {
      focus:   'focused',
      empty:   'empty',
      invalid: 'invalid',
    };

    const cardNumber = elements.create('cardNumber', {
      style:       elementStyles,
      classes:     elementClasses,
      placeholder: 'xxxx-xxxx-xxxx-xxxx',
    });

    cardNumber.mount('#card-number');

    cardNumber.focus();
    setTimeout(() => {
      cardNumber.blur();
    });

    const cardExpiry = elements.create('cardExpiry', {
      style:       elementStyles,
      classes:     elementClasses,
      placeholder: 'mm / yy',
    });

    cardExpiry.mount('#card-exp');

    cardExpiry.focus();
    setTimeout(() => {
      cardExpiry.blur();
    });

    const cardCvc = elements.create('cardCvc', {
      style:       elementStyles,
      classes:     elementClasses,
      placeholder: '',
    });

    cardCvc.mount('#card-cvc');

    cardCvc.focus();
    setTimeout(() => {
      cardCvc.blur();
    });

    const cardNumberErrors = document.getElementById('card-number-errors');
    const cardExpiryErrors = document.getElementById('card-exp-errors');
    const cardCvcErrors = document.getElementById('card-cvc-errors');
    const cardCommonErrors = document.getElementById('card-common-errors');

    cardNumber.addEventListener('change', (e) => {
      document.getElementById('card-number').classList.add('dirty');
      if (e.empty) {
        cardNumberErrors.textContent = 'Card Number is required';
      } else if (e.error) {
        cardNumberErrors.textContent = e.error.message;
      } else {
        cardNumberErrors.textContent = '';
      }
    });

    cardExpiry.addEventListener('change', (e) => {
      document.getElementById('card-exp').classList.add('dirty');
      if (e.empty) {
        cardExpiryErrors.textContent = 'Card expiry date is required';
      } else if (e.error) {
        cardExpiryErrors.textContent = e.error.message;
      } else {
        cardExpiryErrors.textContent = '';
      }
    });

    cardCvc.addEventListener('change', (e) => {
      document.getElementById('card-cvc').classList.add('dirty');
      if (e.empty) {
        cardCvcErrors.textContent = 'Card\'s security code is required';
      } else if (e.error) {
        cardCvcErrors.textContent = e.error.message;
      } else {
        cardCvcErrors.textContent = '';
      }
    });

    document.getElementById('payment-form').addEventListener('submit', async (e) => {
      e.preventDefault();

      // Validate all form
      cardNumber.emit('change', cardNumber);
      cardExpiry.emit('change', cardExpiry);
      cardCvc.emit('change', cardCvc);

      if (cardNumber._empty) {
        cardNumberErrors.textContent = 'Card number is required';
      }

      if (cardExpiry._empty) {
        cardExpiryErrors.textContent = 'Card expiry date is required';
      }

      if (cardCvc._empty) {
        cardCvcErrors.textContent = 'Card\'s security code is required';
      }
      this.$v.$touch();
      if (this.$v.$error || cardNumber._empty
        || cardExpiry._empty || cardCvc._empty) {
        return;
      }
      this.loadingStart();

      this.updateDiscount()
        .then(() => stripe.createPaymentMethod('card', cardNumber))
        // eslint-disable-next-line consistent-return
        .then((result) => {
          if (result.error) {
            cardCommonErrors.textContent = result.error.message;
          } else {
            // Send the token to your server.
            return this.stripeTokenHandler({
              paymentMethodId: result.paymentMethod.id,
            }).then((result) => {
              if (result[0] && result[0].isAxiosError) {
                const error = result[0];

                this.setApiError({ detail: error.response.data.detail });
              } else {
                const { requiresAction, paymentIntentClientSecret } = result;

                if (requiresAction) {
                  return stripe.handleCardAction(paymentIntentClientSecret)
                    .then((result) => {
                      if (result.error) {
                        this.setApiError({ detail: result.error });
                      } else {
                      // The card action has been handled
                        this.stripeTokenHandler({
                          paymentIntentId: result.paymentIntent.id,
                        });
                      }
                    });
                }
              }
              this.loadingStop();

              return result;
            });
          }
        })
        .catch(() => this.loadingStop());
    });


    if (this.isMobilePlatform && this.deviceInfo.platform === 'Android') {
      window.cordova.plugins.payment.GooglePay.canMakePayments(
        [],
        () => {
          this.nativePayment = true;
          this.supportGooglePay = true;
        },
        () => {
          this.nativePayment = false;
        },
      );

      this.renderGooglePayBtn();
    }

    if (this.isMobilePlatform && this.deviceInfo.platform === 'iOS') {
      ApplePay.ApplePay.canMakePayments(
        () => {
          this.nativePayment = true;
          this.supportApplePay = true;
        },
        () => {
          this.nativePayment = false;
        },
      );
    }
  }

  /**
   * Render Google Pay Button in to btn container.
   */
  renderGooglePayBtn() {
    // eslint-disable-next-line no-undef
    const paymentsClient = new google.payments.api.PaymentsClient();

    const btn = paymentsClient.createButton({
      onClick: (() => {
        if (!this.showPickupTimeAlert) {
          this.handleGooglePayPayment();
        }
      }),
    });

    this.googlePayBtnContainer.appendChild(btn);
  }

  /**
   * * Called when token received
   * @param token
   * @returns {Promise<[]>}
   */
  async stripeTokenHandler(token) {
    const result = await to(this.orderPay({
      methodId: token.paymentMethodId,
      intentId: token.paymentIntentId,
    }));

    const [ , paymentResult ] = result;

    if (paymentResult && paymentResult.success) {
      this.updateOrderId();
      this.handlePaymentFinish();
      /* For all platform route name */
      this.$router.push({
        name:  'order-confirmation',
        query: { check_id: paymentResult.checkId },
      });
    }

    return result;
  }

  confirmOrder() {
    // this.$router.push({ path: '/confirmation' });
  }

  /**
   * Ask for new guest token on checkout leave
   */
  async beforeRouteLeave(to, from, next) {
    if (this.isGuest && !this.order) {
      await this.getNewGuestToken();
    }
    next();
  }

  get hasNativePaymentMethod() {
    return this.nativePayment;
  }

  handleNativePayment() {
    const orderRequest = {
      currencyCode:               'USD',
      countryCode:                'US',
      billingAddressRequirement:  'none',
      shippingAddressRequirement: 'none',
      shippingType:               'shipping',
      items:                      [],
    };

    const restaurant = this.restaurants
      .find(restaurant => restaurant.id === this.restaurantId);

    orderRequest.items = [
      {
        label:  'Subtotal',
        amount: this.order.subtotal,
      },
      {
        label:  'Tax',
        amount: this.order.tax,
      },
      {
        label:  'Discount',
        amount: this.order.discountPrice,
      },
      {
        label:  restaurant.name,
        amount: this.order.totalPrice,
      },
    ];

    if (this.isMobilePlatform && this.deviceInfo.platform === 'Android') {
      this.loadingStart();

      window.cordova.plugins.payment.GooglePay.makePaymentRequest(
        [this.order.totalPrice],
        (paymentMethodId) => {
          const response = this.stripeTokenHandler({ paymentMethodId });

          response
            .then(({ requiresAction, paymentIntentClientSecret }) => {
              if (requiresAction) {
                this.stripe.handleCardAction(paymentIntentClientSecret)
                  .then((result) => {
                    if (result.error) {
                      this.setApiError({ detail: result.error });
                    } else {
                      // The card action has been handled
                      this.stripeTokenHandler({
                        paymentIntentId: result.paymentIntent.id,
                      });
                    }
                  });
              }
            }).catch((errorData) => {
              this.setApiError({ detail: errorData });
            }).finally(() => {
              this.loadingStop();
            });
        },
        (errorData) => {
          this.setApiError({ detail: errorData });
          this.loadingStop();
        },
      );
    }

    if (this.isMobilePlatform && this.deviceInfo.platform === 'iOS') {
      this.loadingStart();

      ApplePay.ApplePay.makePaymentRequest(
        orderRequest,
        (paymentMethodId) => {
          const response = this.stripeTokenHandler({ paymentMethodId });

          response
            .then(({ requiresAction, paymentIntentClientSecret }) => {
              ApplePay.ApplePay.completeLastTransaction('success');
              if (requiresAction) {
                this.stripe.handleCardAction(paymentIntentClientSecret)
                  .then((result) => {
                    if (result.error) {
                      this.setApiError({ detail: result.error });
                    } else {
                      // The card action has been handled
                      this.stripeTokenHandler({
                        paymentIntentId: result.paymentIntent.id,
                      });
                    }
                  });
              }
            }).catch((errorData) => {
              ApplePay.ApplePay.completeLastTransaction('failure');
              this.setApiError({ detail: errorData });
            }).finally(() => {
              this.loadingStop();
            });
        },
        (errorData) => {
          ApplePay.ApplePay.completeLastTransaction('failure');
          this.setApiError({ detail: errorData });
          this.loadingStop();
        }
      ).then(data => console.log(data)).catch(data => console.log(data));
    }
  }

  handlePaymentFinish() {
    if (this.isGuest) {
      this.getNewGuestToken();
    }
  }
}
</script>

<style lang="scss" module>
@import '~@/style/variables.scss';
@import '~@/style/helpers.scss';

.checkoutPage {
  background-color: $white;
  padding: 20px 0;
  min-height: 100%;
}

.appGrid {
  position: relative;
  @extend %flex;
  padding: 0 340px 0 15px;
}

.paymentForm {
  padding: 0 60px;

  h3 {
    text-transform: uppercase;
    margin-top: 40px;
    margin-bottom: 11px;
  }
}

.paymentInfo {
  display: flex;
  flex-direction: column;
}

.paymentInfoField {
  display: flex;

  .longField {
    width: 59%;
    padding-right: 30px;
  }

  .shortField {
    width: 41%;
    display: flex;
    position: relative;
  }

  .shortField > div {
    width: calc(50% - 40px);
    margin-right: 40px;
  }

  .longField .formSelect {
    width: 60%;
  }

  .shortField div.formRadio {
    width: 100%;
    margin-right: 0;
    flex-direction: row;
    align-items: flex-end;
  }

  .formSelectGroup {
    margin-top: 0;
  }
}

.checkoutPage .paymentForm .formField label,
.checkoutPage .paymentForm .formSelect label,
.formRadio h6 {
  font-family: 'Gotham', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: $label-green;
}

.checkoutPage .paymentForm .formRadio label {
  font-family: 'Gotham', sans-serif;
  font-weight: 500;
  font-size: 13px;
  color: $black;
}

.checkoutPage .paymentForm .formField.errorField label {
  color: $red-color;
}

.checkoutPage .paymentForm {
  .formField.errorField > div > div::before {
    border-color: $red-color;
  }
}

.paymentInfoField .longField .formSelect.errorSelect label {
  color: $red-color;
}

.checkoutPage .paymentForm {
  .formSelect.errorSelect > div > div::before {
    border-color: $red-color;
  }
}

.errorMessage {
  text-align: left;
  font-size: 12px;
  font-style: italic;
  color: $red-color;
  margin-top: -16px;
  margin-bottom: 14px;
}

.paymentInfoField .shortField .errorMessage {
  position: absolute;
  left: 0;
  top: 70px;
}


.checkoutButtons {
  display: flex;
  justify-content: center;
  margin-top: 100px;
}

.cancelBtn {
  padding: 26px 28px;
  margin: 0 11px;
}

.submitBtn {
  padding: 26px 28px;
  margin: 0 11px;

  .nextIcon {
    transform: rotateY(180deg);
    fill: $white;
    width: 24px;
    height: 24px;
  }

  span {
    padding-right: 16px;
  }
}

.submitBtn,
.cancelBtn {
  cursor: pointer;

  &:before {
    background-color: initial !important;
  }
}

.checkoutPage .paymentForm input {
  font-family: 'Open Sans', sans-serif;
}

.checkoutPage .paymentForm input::placeholder {
  font-family: 'Gotham', sans-serif;
  color: $black;
  opacity: 0.5;
  font-size: 15px;
  font-weight: 300;
}

.mobileTotal {
  display: none;
  margin: 20px 0 0;
}

.nativePaymentContainer {
  align-items: center;
  display: flex;
  justify-content: center;
  align-content: center;
  margin-bottom: 6px;
}

.mobilePayBtn {
  margin: auto;
  outline: none;

  button {
    outline: none;
  }
}

@media (max-width: $kiosk-breakpoint-max) {
  .appGrid {
    padding: 0 15px;
  }

  .mobileTotal {
    display: block;
  }

  .checkoutButtons {
    margin-top: 50px;
  }
}

@media screen and (max-width: $mobile-breakpoint-max) {
  .mobileTotal {
    margin: 0;
  }

  .paymentInfoField {
    flex-direction: column;

    .longField {
      width: 100%;
      padding-right: 0;
    }

    .shortField {
      width: 100%;
      display: flex;
    }

    .shortField > div {
      width: calc(50% - 40px);
      margin-right: 40px;
    }

    .longField .formSelect {
      width: 60%;
    }

    .shortField div.formRadio {
      width: 100%;
      margin-right: 0;
      flex-direction: row;
      align-items: flex-end;
    }

    .formSelectGroup {
      margin-top: 0;
    }
  }

}

@media screen and (max-width: $mobile-breakpoint-max) {
  .appGrid {
    position: static;
  }

  .checkoutPage {
    padding: 20px 0 $mobile-main-button-height;
  }

  .checkoutPage {
    background-color: $light-cart-bg;
  }
  .paymentForm {
    padding: 0 13px;

    h3 {
      font-size: 18px;
      margin-top: 20px;
    }
  }
  .paymentInfoField .longField .formSelect {
    width: 100%;
  }
  .paymentInfoField .shortField > div {
    width: calc(50% - 5px);
    margin-right: 10px;
  }
  .paymentInfoField .shortField > div:last-child {
    width: calc(50% - 5px);
    margin-right: 0;
  }
  .paymentInfoField .shortField > div.formRadio {
    width: 100%;
    margin-right: 0;
  }
}

@media screen and (max-width: 480px) {
  .checkoutButtons {
    margin-top: 40px;
  }
  .cancelBtn {
    margin: 0 6px;
    padding: 16px 26px;

    div {
      font-size: 14px;
      line-height: 14px;
    }
  }

  .submitBtn {
    margin: 0 6px;
    padding: 16px 26px;

    div {
      font-size: 14px;
      line-height: 14px;
    }

    .nextIcon {
      transform: rotateY(180deg);
      fill: $white;
      width: 14px;
      height: 14px;
    }

    span {
      padding-right: 10px;
    }
  }
  .paymentForm {
    h3 {
      font-size: 1rem;
      margin: 0;
    }
  }
}

.googlePayBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
}

.applePayBtn {
  width: 240px;
  height: 40px;

  @supports (-webkit-appearance: -apple-pay-button) {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 15px;
    -webkit-appearance: -apple-pay-button;
    -apple-pay-button-style: black;
    -apple-pay-button-type: buy;
  }

  @supports not (-webkit-appearance: -apple-pay-button) {
    display: inline-block;
    background-size: 100% 60%;
    background-repeat: no-repeat;
    background-position: 50% 50%;
    border-radius: 5px;
    padding: 0px;
    box-sizing: border-box;
    min-width: 200px;
    min-height: 32px;
    max-height: 64px;

    background-image: -webkit-named-image(apple-pay-logo-white);
    background-color: black;
  }
}
</style>

<style scoped>

/* Stripe component styles */

#card-element {
  display: flex;
  width: 100%;
}

#card-element .formField {
  height: 50px;
  position: relative;
  margin-bottom: 30px;
}


#card-element .baseline {
  position: absolute;
  width: calc(100% - 30px);
  height: 1px;
  left: 0;
  bottom: 0;
  background-color: rgba(0,0,0,.42);
}

#card-element .baseline::after {
  bottom: 0;
  content: "";
  left: 0;
  position: absolute;
  transition: .3s cubic-bezier(.25,.8,.5,1);
  width: 100%;
  border-color: currentcolor;
  border-style: solid;
  border-width: thin 0 0;
  transform: scaleX(0);
}

#card-element label {
  position: absolute;
  width: 100%;
  left: 0;
  top: 18px;
  font-family: 'Gotham', sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #39AA40;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transform-origin: top left;
  pointer-events: none;
  cursor: text;
  transition-property: color, transform;
  transition-duration: 0.3s;
  transition-timing-function: cubic-bezier(0.165, 0.84, 0.44, 1);
}

#card-element .input {
  position: absolute;
  width: 100%;
  left: 0;
  bottom: 0;
  padding-bottom: 7px;
  background-color: transparent;
}

#card-element .input::-webkit-input-placeholder {
  color: transparent;
  transition: color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

#card-element .input::-moz-placeholder {
  color: transparent;
  transition: color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

#card-element .input:-ms-input-placeholder {
  color: transparent;
  transition: color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

#card-element .input.StripeElement {
  opacity: 0;
  transition: opacity 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  will-change: opacity;
}

#card-element .input.focused,
#card-element .input:not(.empty) {
  opacity: 1;
}

#card-element .input.focused::-webkit-input-placeholder,
#card-element .input:not(.empty)::-webkit-input-placeholder {
  color: #CFD7DF;
}

#card-element .input.focused::-moz-placeholder,
#card-element .input:not(.empty)::-moz-placeholder {
  color: #CFD7DF;
}

#card-element .input.focused:-ms-input-placeholder,
#card-element .input:not(.empty):-ms-input-placeholder {
  color: #CFD7DF;
}

#card-element .input.focused + label,
#card-element .input:not(.empty) + label {
  color: #39AA40;
  transform: scale(0.75) translateY(-18px);
  cursor: default;
}

#card-element .input.focused + label {
  color: #39AA40;
}

#card-element .input.invalid + label,
#card-element .input.empty.dirty + label{
  color: #D0021B;
}

#card-element .input.focused + label + .baseline {
  bottom: -1px;
}

#card-element .input.focused + label + .baseline::after {
  transform: scaleX(1);
  border-width: 1px;
  border-color: #39AA40;
}

#card-element .input.invalid + label + .baseline::after,
#card-element .input.empty.dirty + label + .baseline::after{
  transform: scaleX(1);
  border-color: #D0021B;
}

.card-error-message {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  font-family: 'Gotham', sans-serif;
  text-align: left;
  font-size: 12px;
  font-weight: 300;
  font-style: italic;
  color: #D0021B;
}

.card-error-message.common-messages {
  top: calc(100% + 30px);
}

@media screen and (max-width: 480px) {
  #card-element {
    flex-direction: column;
  }
  #card-element .formField {
    width: 100%;
  }
  #card-element .baseline {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/CheckoutPayment.vue